import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./OfficeAssignedTransfersPanel.css";

// OFFICE_ASSIGNED_OFFICE_TABLE_WAREHOUSE_PICK_UI_V2

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function formatQty(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

function norm(value) {
  return String(value || "").trim().toUpperCase();
}

function cleanBarcode(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[*/\\]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function parseBarcode(raw) {
  const cleaned = cleanBarcode(raw);
  const parts = cleaned.split("-").map((x) => x.trim()).filter(Boolean);

  if (parts.length >= 6) {
    return {
      raw,
      cleaned,
      productCode: parts.slice(0, 4).join("-").toUpperCase(),
      color: parts[4].toUpperCase(),
      size: parts[5].toUpperCase(),
      isFull: true,
    };
  }

  if (parts.length >= 4) {
    return {
      raw,
      cleaned,
      productCode: parts.slice(0, 4).join("-").toUpperCase(),
      color: "",
      size: "",
      isFull: false,
    };
  }

  return {
    raw,
    cleaned,
    productCode: cleaned.toUpperCase(),
    color: "",
    size: "",
    isFull: false,
  };
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function OfficeAssignedDetailModal({ row, user, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [scanRaw, setScanRaw] = useState("");
  const [draft, setDraft] = useState({
    productCode: "",
    color: "",
    size: "",
    brand: "HANGTEN",
    qty: 1,
  });

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const officeRows = detail?.summary || [];
  const pickedRows = detail?.pickedItems || [];

  const selectedOfficeRows = useMemo(() => {
    const code = norm(draft.productCode);
    if (!code) return [];
    return officeRows.filter((item) => norm(item.productCode) === code);
  }, [officeRows, draft.productCode]);

  const colorOptions = useMemo(() => {
    return unique(selectedOfficeRows.map((item) => norm(item.color)));
  }, [selectedOfficeRows]);

  const sizeOptions = useMemo(() => {
    const color = norm(draft.color);
    return unique(
      selectedOfficeRows
        .filter((item) => !color || norm(item.color) === color)
        .map((item) => norm(item.size))
    );
  }, [selectedOfficeRows, draft.color]);

  const brandOptions = useMemo(() => {
    return unique(selectedOfficeRows.map((item) => norm(item.brand)));
  }, [selectedOfficeRows]);

  const incompleteRows = useMemo(() => {
    return officeRows.filter((item) => Number(item.remainingQty || 0) > 0);
  }, [officeRows]);

  const completeRows = useMemo(() => {
    return officeRows.filter((item) => Number(item.remainingQty || 0) <= 0);
  }, [officeRows]);

  async function loadDetail() {
    if (!row?.id) return;

    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/office-assigned-transfers/${row.id}/detail`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Load detail failed");
      }

      setDetail(data.transfer || null);
    } catch (err) {
      setError(err.message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }

  function applyParsedBarcode(parsed) {
    if (!parsed.productCode) {
      setError("Barcode မမှန်ပါ။");
      return;
    }

    const rowsForCode = officeRows.filter(
      (item) => norm(item.productCode) === norm(parsed.productCode)
    );

    if (rowsForCode.length === 0) {
      setError("ERROR: ဒီ product code က Office transfer list ထဲမှာ မပါပါ။");
      return;
    }

    if (parsed.isFull) {
      const exact = rowsForCode.find(
        (item) =>
          norm(item.color) === norm(parsed.color) &&
          norm(item.size) === norm(parsed.size)
      );

      if (!exact) {
        setError("ERROR: Code တူပေမယ့် color/size က Office list နဲ့ မကိုက်ပါ။");
        return;
      }

      if (Number(exact.remainingQty || 0) <= 0) {
        setError("ERROR: Office required qty ပြည့်ပြီးသားပါ။ Extra qty မထည့်နိုင်ပါ။");
        return;
      }

      setDraft({
        productCode: norm(parsed.productCode),
        color: norm(parsed.color),
        size: norm(parsed.size),
        brand: norm(exact.brand || "HANGTEN"),
        qty: 1,
      });
      setError("");
      return;
    }

    const availableRows = rowsForCode.filter((item) => Number(item.remainingQty || 0) > 0);

    if (availableRows.length === 0) {
      setError("ERROR: ဒီ product code အတွက် required qty ပြည့်ပြီးသားပါ။");
      return;
    }

    const first = availableRows[0];

    setDraft({
      productCode: norm(parsed.productCode),
      color: availableRows.length === 1 ? norm(first.color) : "",
      size: availableRows.length === 1 ? norm(first.size) : "",
      brand: norm(first.brand || "HANGTEN"),
      qty: 1,
    });

    setError("");
  }

  async function handleBarcodeSubmit() {
    const parsed = parseBarcode(scanRaw);

    if (!parsed.productCode) {
      setError("Barcode / product code မမှန်ပါ။");
      return;
    }

    const rowsForCode = officeRows.filter(
      (item) => norm(item.productCode) === norm(parsed.productCode)
    );

    if (rowsForCode.length === 0) {
      setError("ERROR: ဒီ product code က Office transfer list ထဲမှာ မပါပါ။");
      return;
    }

    if (!parsed.isFull) {
      applyParsedBarcode(parsed);
      return;
    }

    const exact = rowsForCode.find(
      (item) =>
        norm(item.color) === norm(parsed.color) &&
        norm(item.size) === norm(parsed.size)
    );

    if (!exact) {
      setError("ERROR: Code တူပေမယ့် color/size က Office list နဲ့ မကိုက်ပါ။");
      return;
    }

    if (Number(exact.remainingQty || 0) <= 0) {
      setError("ERROR: Office required qty ပြည့်ပြီးသားပါ။ Extra qty မထည့်နိုင်ပါ။");
      return;
    }

    await pickItem(
      {
        productCode: norm(parsed.productCode),
        color: norm(parsed.color),
        size: norm(parsed.size),
        brand: norm(exact.brand || "HANGTEN"),
        qty: 1,
      },
      scanRaw
    );
  }


  
  // OFFICE_ASSIGNED_BARCODE_ENTER_AUTO_PICK_V3
  
  async function pickItem(overrideDraft = null, overrideScanRaw = "") {
    const inputDraft = overrideDraft || draft;
    const activeScanRaw = overrideScanRaw || scanRaw;

    try {
      setActionLoading("pick");
      setError("");
      setSuccess("");

      let activeDraft = {
        productCode: norm(inputDraft.productCode),
        color: norm(inputDraft.color),
        size: norm(inputDraft.size),
        brand: norm(inputDraft.brand || "HANGTEN"),
        qty: Number(inputDraft.qty || 1),
      };

      if (!activeDraft.productCode && activeScanRaw) {
        const parsed = parseBarcode(activeScanRaw);
        activeDraft.productCode = norm(parsed.productCode);
        activeDraft.color = norm(parsed.color);
        activeDraft.size = norm(parsed.size);
      }

      const rowsForCode = officeRows.filter(
        (item) => norm(item.productCode) === activeDraft.productCode
      );

      if (rowsForCode.length === 0) {
        throw new Error("ERROR: ဒီ product code က Office transfer list ထဲမှာ မပါပါ။");
      }

      if (!activeDraft.color && rowsForCode.length === 1) {
        activeDraft.color = norm(rowsForCode[0].color);
      }

      const rowsForColor = rowsForCode.filter(
        (item) => !activeDraft.color || norm(item.color) === activeDraft.color
      );

      if (!activeDraft.size && rowsForColor.length === 1) {
        activeDraft.size = norm(rowsForColor[0].size);
      }

      if (!activeDraft.brand && rowsForCode[0]?.brand) {
        activeDraft.brand = norm(rowsForCode[0].brand);
      }

      if (!activeDraft.productCode || !activeDraft.color || !activeDraft.size || !activeDraft.brand) {
        throw new Error("Product / Color / Size / Brand ရွေးရန်လိုပါတယ်။");
      }

      const match = officeRows.find(
        (item) =>
          norm(item.productCode) === activeDraft.productCode &&
          norm(item.color) === activeDraft.color &&
          norm(item.size) === activeDraft.size &&
          norm(item.brand) === activeDraft.brand
      );

      if (!match) {
        throw new Error("ERROR: ဒီ code/color/size က Office required list ထဲမှာ မပါပါ။");
      }

      if (Number(match.remainingQty || 0) <= 0) {
        throw new Error("ERROR: Office required qty ပြည့်ပြီးသားပါ။ Extra qty မထည့်နိုင်ပါ။");
      }

      if (Number(activeDraft.qty || 0) > Number(match.remainingQty || 0)) {
        throw new Error("ERROR: Qty over ဖြစ်နေပါတယ်။ Required remaining ထက် ပိုမထည့်နိုင်ပါ။");
      }

      const res = await fetch(`${API_BASE}/api/erp/office-assigned-transfers/${row.id}/pick`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: user?.username || "warehouse_staff",
          productCode: activeDraft.productCode,
          color: activeDraft.color,
          size: activeDraft.size,
          brand: activeDraft.brand,
          qty: Number(activeDraft.qty || 1),
          scanRaw: activeScanRaw,
          note: "Picked from Warehouse side of Office Assigned Transfer",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Pick item failed");
      }

      setSuccess("Item picked");
      setDetail(data.transfer || null);
      setScanRaw("");
      setDraft({
        productCode: "",
        color: "",
        size: "",
        brand: "HANGTEN",
        qty: 1,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  async function removePicked(item) {
    try {
      setActionLoading(item.id);
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office-assigned-transfers/${row.id}/pick/${item.id}`,
        { method: "DELETE" }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Remove picked item failed");
      }

      setSuccess("Picked item removed");
      await loadDetail();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  async function dispatchPicked() {
    if (pickedRows.length === 0) {
      setError("Picked item မရှိသေးပါ။ Warehouse side မှာ scan/manual အရင်ထည့်ပါ။");
      return;
    }

    if (incompleteRows.length > 0) {
      const ok = window.confirm(
        "Office list ထဲက item တချို့ မပြည့်သေးပါ။ ရှိတဲ့ picked item တွေကိုပဲ dispatch လုပ်မလား?"
      );
      if (!ok) return;
    }

    try {
      setActionLoading("dispatch");
      setError("");
      setSuccess("");

      const res = await fetch(
        `${API_BASE}/api/erp/office-assigned-transfers/${row.id}/dispatch-picked`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: user?.username || "warehouse_staff",
            dispatchNote:
              incompleteRows.length > 0
                ? "Partial dispatch from Office Assigned Transfer"
                : "Full dispatch from Office Assigned Transfer",
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Dispatch picked transfer failed");
      }

      setSuccess("Transfer dispatched to shop");
      setDetail(data.transfer || null);

      if (typeof onChanged === "function") await onChanged();

      setSuccess("Transfer dispatched to shop. Sent Office Transfer List ထဲမှာ ပေါ်ပါမယ်။");
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  return (
    <div className="officeAssignedModalOverlay" onMouseDown={onClose}>
      <div className="officeAssignedModal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="officeAssignedModalHead">
          <div>
            <div className="officeAssignedTransfers__tag">📦 OFFICE ASSIGNED DETAIL</div>
            <h2>{detail?.transferNo || row.transferNo}</h2>
            <p>
              {detail?.fromLocationCode || row.fromLocationCode} →{" "}
              {detail?.toLocationCode || row.toLocationCode}
            </p>
          </div>

          <button type="button" onClick={onClose} className="officeAssignedModalClose">
            ×
          </button>
        </div>

        {loading && <div className="officeAssignedEmpty">Loading detail...</div>}
        {error && <div className="officeAssignedAlert officeAssignedAlert--error">{error}</div>}
        {success && <div className="officeAssignedAlert officeAssignedAlert--success">{success}</div>}

        {detail && (
          <>
            <div className="officeAssignedDetailStats">
              <span>Status: {detail.status}</span>
              <span>Office Lines: {officeRows.length}</span>
              <span>Picked Lines: {pickedRows.length}</span>
              <span>Complete: {completeRows.length}</span>
              <span>Remaining: {incompleteRows.length}</span>
            </div>

            <section className="officeSideBox">
              <h3>Office Required List</h3>
              <p>Office ကပို့ခိုင်းထားတဲ့ item list ဖြစ်ပါတယ်။ ဒီ section မှာ scan မရှိပါ။</p>

              <div className="officeAssignedTableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Brand</th>
                      <th>Required</th>
                      <th>Picked</th>
                      <th>Remain</th>
                    </tr>
                  </thead>

                  <tbody>
                    {officeRows.map((item) => (
                      <tr key={`${item.productCode}-${item.color}-${item.size}`}>
                        <td>{item.productCode}</td>
                        <td>{item.color}</td>
                        <td>{item.size}</td>
                        <td>{item.brand}</td>
                        <td>{formatQty(item.requiredQty)}</td>
                        <td>{formatQty(item.pickedQty)}</td>
                        <td className={item.remainingQty > 0 ? "officeRemainBad" : "officeRemainOk"}>
                          {formatQty(item.remainingQty)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="warehouseSideBox">
              <div className="warehouseSideHead">
                <div>
                  <h3>Warehouse Actual Pick / Scan</h3>
                  <p>
                    Barcode scan သို့မဟုတ် manual ဖြင့် Office list အတိုင်း pick လုပ်ပါ။
                    Code-only barcode ဖြစ်ရင် color/size ကို dropdown မှရွေးပါ။
                  </p>
                </div>
              </div>

              <div className="warehousePickEntry">
                <label>
                  <span>Barcode / Product Code</span>
                  <input
                    value={scanRaw}
                    onChange={(e) => setScanRaw(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleBarcodeSubmit();
                      }
                    }}
                    placeholder="10410-030-001-01 or 10410-030-001-01-001-XL"
                  />
                </label>

                <button
                  type="button"
                  onClick={handleBarcodeSubmit}
                >
                  Add / Read
                </button>
              </div>

              <div className="warehousePickDraft">
                <label>
                  <span>Product</span>
                  <input
                    value={draft.productCode}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        productCode: norm(e.target.value),
                        color: "",
                        size: "",
                      }))
                    }
                    placeholder="Product Code"
                  />
                </label>

                <label>
                  <span>Color</span>
                  <select
                    value={draft.color}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, color: norm(e.target.value), size: "" }))
                    }
                  >
                    <option value="">Select Color</option>
                    {colorOptions.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Size</span>
                  <select
                    value={draft.size}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, size: norm(e.target.value) }))
                    }
                  >
                    <option value="">Select Size</option>
                    {sizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Brand</span>
                  <select
                    value={draft.brand}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, brand: norm(e.target.value) }))
                    }
                  >
                    {(brandOptions.length > 0 ? brandOptions : ["HANGTEN", "PRETTYFIT"]).map(
                      (brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label>
                  <span>Qty</span>
                  <input
                    type="number"
                    min="1"
                    value={draft.qty}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, qty: e.target.value }))
                    }
                  />
                </label>

                <button
                  type="button"
                  onClick={pickItem}
                  disabled={actionLoading === "pick"}
                >
                  {actionLoading === "pick" ? "Adding..." : "Add Pick"}
                </button>
              </div>

              <h3 className="pickedTitle">Warehouse Actual Picked List</h3>

              <div className="officeAssignedTableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Brand</th>
                      <th>Picked Qty</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pickedRows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="officeAssignedEmptyCell">
                          Warehouse picked item မရှိသေးပါ။
                        </td>
                      </tr>
                    ) : (
                      pickedRows.map((item) => (
                        <tr key={item.id}>
                          <td>{item.productCode}</td>
                          <td>{item.color}</td>
                          <td>{item.size}</td>
                          <td>{item.brand}</td>
                          <td>{formatQty(item.pickedQty)}</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removePicked(item)}
                              disabled={actionLoading === item.id}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {incompleteRows.length > 0 && (
              <div className="officeAssignedAlert officeAssignedAlert--warning">
                Office required list မပြည့်သေးသော item ရှိသည်။ Warehouse stock မရှိရင် ရှိတဲ့ item ကိုသာ dispatch လုပ်နိုင်ပြီး office warning flow ကို နောက် step မှာချိတ်မယ်။
              </div>
            )}

            <div className="officeAssignedModalFooter">
              <button type="button" onClick={loadDetail}>
                Refresh Detail
              </button>

              <button
                type="button"
                className="officeAssignedDispatchBtn"
                onClick={dispatchPicked}
                disabled={actionLoading === "dispatch" || detail.status !== "APPROVED"}
              >
                {actionLoading === "dispatch" ? "Dispatching..." : "🚚 Dispatch Picked Items"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function OfficeAssignedTransfersPanel({ user, selectedLocation, onChanged }) {
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadRows() {
    if (!selectedLocation) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        status: "ALL",
        warehouse: selectedLocation,
        limit: "100",
      });

      const res = await fetch(`${API_BASE}/api/erp/office-transfer-plan/list?${params.toString()}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Load office assigned transfers failed");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const pendingRows = (Array.isArray(rows) ? rows : []).filter((row) =>
    String(row.status || "").toUpperCase() === "APPROVED"
  );

  const sentRows = (Array.isArray(rows) ? rows : []).filter((row) =>
    ["DISPATCHED", "ACCEPTED", "COMPLETED", "RECEIVED"].includes(
      String(row.status || "").toUpperCase()
    )
  );

  // OFFICE_TRANSFER_ADD_PICK_AND_SENT_LIST_V1
  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  return (
    <section className="officeAssignedTransfers">
      <div className="officeAssignedTransfers__head">
        <div>
          <div className="officeAssignedTransfers__tag">📦 OFFICE ASSIGNED</div>
          <h3>Office Assigned Transfers</h3>
          <p>
            Office မှ Excel / manual upload ဖြင့် သတ်မှတ်ထားသော transfer list များ။
            Manual Warehouse Transfer list နှင့် သီးခြားဖြစ်သည်။
          </p>
        </div>

        <button type="button" onClick={loadRows} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="officeAssignedAlert officeAssignedAlert--error">{error}</div>}

      {rows.length === 0 ? (
        <div className="officeAssignedEmpty">
          {selectedLocation} အတွက် Office Transfer မရှိသေးပါ။
        </div>
      ) : (
        <>
          <div className="officeAssignedSubHead">
            <h4>Pending Office Transfers</h4>
            <span>{pendingRows.length} pending</span>
          </div>

          {pendingRows.length === 0 ? (
            <div className="officeAssignedEmpty">
              ပို့ရန်ကျန်သော Office Assigned transfer မရှိပါ။
            </div>
          ) : (
            <div className="officeAssignedList">
              {pendingRows.map((row) => (
                <div key={row.id} className="officeAssignedCard">
                  <div className="officeAssignedCard__top">
                    <div>
                      <b>{row.transferNo}</b>
                      <span>{row.fromLocationCode} → {row.toLocationCode}</span>
                    </div>

                    <strong>{row.status}</strong>
                  </div>

                  <div className="officeAssignedMeta">
                    <span>Batch: {row.batchNo || "-"}</span>
                    <span>Created: {formatDate(row.createdAt)}</span>
                    <span>Approved: {formatDate(row.approvedAt)}</span>
                    {row.officeNote && <span>Note: {row.officeNote}</span>}
                  </div>

                  <div className="officeAssignedActions">
                    <button type="button" onClick={() => setSelectedRow(row)}>
                      View / Open
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="officeAssignedSubHead officeAssignedSubHead--sent">
            <h4>Sent Office Transfer List</h4>
            <span>{sentRows.length} sent/received</span>
          </div>

          {sentRows.length === 0 ? (
            <div className="officeAssignedEmpty">
              Warehouse က shop ကို ပို့ပြီးသား Office transfer မရှိသေးပါ။
            </div>
          ) : (
            <div className="officeAssignedList officeAssignedList--sent">
              {sentRows.map((row) => (
                <div key={row.id} className="officeAssignedCard officeAssignedCard--sent">
                  <div className="officeAssignedCard__top">
                    <div>
                      <b>{row.transferNo}</b>
                      <span>{row.fromLocationCode} → {row.toLocationCode}</span>
                    </div>

                    <strong>{row.status}</strong>
                  </div>

                  <div className="officeAssignedMeta">
                    <span>Batch: {row.batchNo || "-"}</span>
                    <span>Created: {formatDate(row.createdAt)}</span>
                    <span>Dispatched: {formatDate(row.dispatchedAt)}</span>
                    {row.officeNote && <span>Note: {row.officeNote}</span>}
                  </div>

                  <div className="officeAssignedActions">
                    <button type="button" onClick={() => setSelectedRow(row)}>
                      View Status
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectedRow && (
        <OfficeAssignedDetailModal
          row={selectedRow}
          user={user}
          onClose={() => {
            setSelectedRow(null);
            loadRows();
          }}
          onChanged={async () => {
            await loadRows();
            if (typeof onChanged === "function") await onChanged();
          }}
        />
      )}
    </section>
  );
}
